import React from "react";
import { HiFolder, HiOutlineDotsVertical, HiOutlineShare, HiOutlineTrash, HiCheck } from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";

const FolderCard = React.forwardRef(({ folder, index = 0, onClick, view = "grid", onRename, onShare, onDelete, isSelected, onToggleSelect }, ref) => {
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const date = new Date(folder.createdAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });

  const itemsText = `${folder.itemsCount || 0} items`;

  if (view === "list") {
    return (
      <motion.div
        ref={ref}
        onClick={onClick}
        className={`flex items-center px-6 py-1.5 hover:bg-slate-100/50 transition-all duration-200 cursor-pointer group border-b border-slate-100 last:border-none ${isSelected ? "bg-indigo-50/50" : ""}`}
      >
        <div
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          className={`mr-4 w-4 h-4 rounded border transition-all flex items-center justify-center shrink-0 ${isSelected ? "bg-indigo-600 border-indigo-600 scale-110" : "border-slate-300 opacity-0 group-hover:opacity-100 scale-100"
            }`}
        >
          <AnimatePresence>
            {isSelected && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
              >
                <HiCheck size={10} className="text-white" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="flex-1 flex items-center gap-3">
          <motion.div 
            className="w-8 h-8 flex items-center justify-center text-amber-500 group-hover:scale-110 transition-all"
          >
            <HiFolder size={20} />
          </motion.div>
          <h4 className="font-bold text-xs text-slate-800 truncate">
            {folder.name.length > 30 ? folder.name.substring(0, 30) + "..." : folder.name}
          </h4>
        </div>



        <div className="w-32 hidden lg:block text-[11px] font-medium text-slate-400">
          File folder
        </div>

        <div className="w-24 text-right text-[11px] font-bold text-slate-400">
          {formatFileSize(folder.size)}
        </div>

        <div className="w-32 text-right text-[11px]  text-slate-400">
          {folder.createdAt ? new Date(folder.createdAt).toLocaleString("en-In") : "-"}
        </div>

        <div className="ml-4 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
          {onShare && <button onClick={(e) => { e.stopPropagation(); onShare?.(folder); }} className="p-1 px-1.5 rounded-md hover:bg-white hover:shadow-sm text-slate-400 hover:text-indigo-600 transition-all"><HiOutlineShare size={14} /></button>}
          {onRename && <button onClick={(e) => { e.stopPropagation(); onRename?.(folder); }} className="p-1 px-1.5 rounded-md hover:bg-white hover:shadow-sm text-slate-400 hover:text-slate-900 transition-all"><HiOutlineDotsVertical size={14} /></button>}
          {onDelete && <button onClick={(e) => { e.stopPropagation(); onDelete?.(folder); }} className="p-1 px-1.5 rounded-md hover:bg-white hover:shadow-sm text-slate-400 hover:text-red-500 transition-all"><HiOutlineTrash size={14} /></button>}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={ref}
      onClick={onClick}
      className={`group cursor-pointer flex flex-col items-center gap-0.5 p-1 hover:bg-slate-100/50 rounded-xl transition-all duration-300 relative hover:z-50 ${isSelected ? "bg-indigo-50/80 ring-2 ring-indigo-500/20" : ""}`}
    >
      {/* Checkbox for Grid View */}
      <div
        onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
        className={`absolute top-2 left-2 z-[60] w-5 h-5 rounded-lg border-2 transition-all flex items-center justify-center ${isSelected ? "bg-indigo-600 border-indigo-600 scale-110 shadow-lg shadow-indigo-200" : "bg-white border-slate-200 opacity-0 group-hover:opacity-100 scale-90"
          }`}
      >
        <AnimatePresence>
          {isSelected && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
            >
              <HiCheck size={12} className="text-white" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <motion.div 
        className="relative w-16 h-16 flex items-center justify-center transition-all duration-300"
      >
        {/* Main Folder Icon */}
        <div className="relative">
          <motion.div
            whileHover={{ scale: 1.05, rotate: 2 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <HiFolder
              size={64}
              className="text-amber-400 drop-shadow-[0_1px_2px_rgba(0,0,0,0.1)] transition-all duration-300"
            />
          </motion.div>

          {/* Item Count Overlay - Bottom Left Flap style */}
          <div className="absolute bottom-[10%] left-[8%] bg-amber-500/20 px-1.5 py-0.5 rounded text-[10px] font-sans text-amber-900 border border-amber-600/20 backdrop-blur-[2px]">
            {folder.itemsCount || 0}
          </div>
        </div>
      </motion.div>

      {/* Hover Actions - 2x2 Grid on Mobile, Vertical on Desktop */}
      <div className="absolute top-1 right-1 grid grid-cols-2 md:flex md:flex-col items-center opacity-0 group-hover:opacity-100 transition-all duration-300 z-50 gap-1 md:gap-0.5">
        {onShare && (
          <button
            onClick={(e) => { e.stopPropagation(); onShare?.(folder); }}
            className="w-6 h-6 md:w-5 md:h-5 bg-white shadow-xl rounded-lg flex items-center justify-center text-slate-600 hover:text-indigo-600 transition-all border border-slate-100 hover:scale-110 active:scale-95"
            title="Share"
          >
            <HiOutlineShare size={12} />
          </button>
        )}
        {onRename && (
          <button
            onClick={(e) => { e.stopPropagation(); onRename?.(folder); }}
            className="w-6 h-6 md:w-5 md:h-5 bg-white shadow-xl rounded-lg flex items-center justify-center text-slate-600 hover:text-slate-900 transition-all border border-slate-100 hover:scale-110 active:scale-95"
            title="Rename"
          >
            <HiOutlineDotsVertical size={12} />
          </button>
        )}
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete?.(folder); }}
            className="w-6 h-6 md:w-5 md:h-5 bg-white shadow-xl rounded-lg flex items-center justify-center text-rose-500 hover:bg-rose-50 transition-all border border-slate-100 hover:scale-110 active:scale-95"
            title="Delete"
          >
            <HiOutlineTrash size={12} />
          </button>
        )}
      </div>

      <div className="flex flex-col items-center text-center w-full px-0.5 relative">
        <span className="text-slate-900 block w-full text-[12px] tracking-tight truncate px-1">
          {folder.name}
        </span>

        {/* Windows-style Metadata Tooltip - Ultra Compact */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-0.5 w-36 bg-white/95 backdrop-blur-sm border border-slate-200 shadow-2xl rounded-lg p-1.5 z-[100] pointer-events-none opacity-0 group-hover:opacity-100 transition-all duration-200 text-left scale-95 group-hover:scale-100 origin-top">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-1 mb-0.5">
              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">Folder Details</span>
            </div>

            <div className="flex flex-col leading-tight">
              <span className="text-[7.5px] font-medium text-slate-400 uppercase tracking-tighter">Contents</span>
              <span className="text-[9px] font-semibold text-slate-700">{folder.itemsCount || 0} items</span>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[7.5px] font-medium text-slate-400 uppercase tracking-tighter">Size</span>
              <span className="text-[9px] font-semibold text-slate-700">{formatFileSize(folder.size)}</span>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[7.5px] font-medium text-slate-400 uppercase tracking-tighter">Modified</span>
              <span className="text-[9px] font-semibold text-slate-700">{date}</span>
            </div>
          </div>
          {/* Tooltip Arrow */}
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white border-t border-l border-slate-200 rotate-45" />
        </div>
      </div>
    </motion.div>
  );
});



export default FolderCard;
